import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Prompt } from '@app-libs/model';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PromptsApiService } from '../../../services/api/prompts-api.service';

@Component({
  selector: 'app-prompt-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatTooltipModule,
    MatAutocompleteModule,
    FormsModule,
  ],
  templateUrl: './prompt-dialog.component.html',
  styleUrls: ['./prompt-dialog.component.scss'],
})
export class PromptDialogComponent implements OnInit {
  promptForm: FormGroup;
  isEditMode = false;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private promptsService: PromptsApiService,
    private dialogRef: MatDialogRef<PromptDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { mode: 'create' | 'edit'; prompt?: Prompt }
  ) {
    this.isEditMode = data.mode === 'edit';
    this.promptForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', Validators.maxLength(500)],
      category: ['', Validators.required],
      tags: this.fb.array([]),
      instruction: ['', [Validators.required, Validators.minLength(10)]],
      permission_type: ['global', Validators.required],
      is_active: [true],
    });
  }

  ngOnInit(): void {
    if (this.isEditMode && this.data.prompt) {
      this.promptForm.patchValue({
        ...this.data.prompt,
        tags: this.data.prompt.tags || [],
      });
      this.data.prompt.tags?.forEach((tag) => this.addTag(tag));
    }
  }

  get tags(): FormArray {
    return this.promptForm.get('tags') as FormArray;
  }

  /**
   * Add a tag to the form array.
   * @param tag The tag string.
   */
  addTag(tag = ''): void {
    if (tag && !this.tags.value.includes(tag)) {
      this.tags.push(this.fb.control(tag));
    }
  }

  /**
   * Remove a tag by index.
   * @param index The index of the tag to remove.
   */
  removeTag(index: number): void {
    this.tags.removeAt(index);
  }

  /**
   * Submit the form to create or update the prompt.
   */
  submit(): void {
    if (this.promptForm.invalid) {
      this.promptForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.promptForm.value;

    if (this.isEditMode && this.data.prompt) {
      this.promptsService
        .updatePrompt(this.data.prompt.prompt_id, formValue)
        .toPromise()
        .then((result) => {
          this.isSubmitting = false;
          if (result) {
            this.dialogRef.close(true);
          }
        })
        .catch(() => {
          this.isSubmitting = false;
        });
    } else {
      this.promptsService
        .createPrompt(formValue)
        .toPromise()
        .then((result) => {
          this.isSubmitting = false;
          if (result) {
            this.dialogRef.close(true);
          }
        })
        .catch(() => {
          this.isSubmitting = false;
        });
    }
  }

  /**
   * Close the dialog without saving.
   */
  cancel(): void {
    this.dialogRef.close(false);
  }

  handleTagInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    if (value) {
      this.addTag(value);
    }
    input.value = '';
  }

  clearInput(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.value = '';
  }
}
